import React, { useEffect, useMemo } from 'react'
import { Radio, Typography, Form, Alert, Space } from 'antd'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import LoadingWrapper from '../../../../../components/Misc/LoadingWrapper'
import { normalizedConfigSelector } from '../../../../../redux/config/selectors'
import AccountSelect from '../../../../../features/account/components/AccountSelect'
import { useCtxCompanyGroupSettings } from '../../../../../features/companyGroup/queries/companyGroupSetting/useCtxCompanyGroupSettings'
import { useCtxUpdateCompanyGroupSettings } from '../../../../../features/companyGroup/queries/companyGroupSetting/useCtxUpdateCompanyGroupSettings'
import {
  GroupSettingData,
  GroupSettingDataSource
} from '../../../../../features/companyGroup/types/CompanyGroupSettings'
import { ReportDataType } from '../../../../../redux/context/reports/types'

const Figures = () => {
  const [form] = Form.useForm<GroupSettingData>()
  const { t } = useTranslation()
  const budgetSource = Form.useWatch(['budget', 'source'], form)
  const actualsSource = Form.useWatch(['actuals', 'source'], form)
  const {
    financialStatement: { plAssociateProfitRowId, plMinoritySharesRowId, bsMinoritySharesRowId, bsAssociateSharesRowId }
  } = useSelector(normalizedConfigSelector)

  const { data: groupSettings, isLoading } = useCtxCompanyGroupSettings()
  const { mutate: updateGroupSettings } = useCtxUpdateCompanyGroupSettings()

  const accountsRequired = useMemo(
    () => [budgetSource, actualsSource].includes(GroupSettingDataSource.subsidiaries),
    [actualsSource, budgetSource]
  )

  const groupSettingsConfig = {
    plAssociateProfitAccountCode: plAssociateProfitRowId,
    plMinoritySharesAccountCode: plMinoritySharesRowId,
    bsMinoritySharesAccountCode: bsMinoritySharesRowId,
    bsAssociateSharesAccountCode: bsAssociateSharesRowId
  }

  const handleSubmit = (data: GroupSettingData) => {
    const normalized =
      data.actuals.source === GroupSettingDataSource.subsidiaries && data.budget.source === GroupSettingDataSource.group
        ? { ...data, budget: { ...data.budget, source: GroupSettingDataSource.subsidiaries } }
        : data
    updateGroupSettings(normalized)
  }

  const getInitValues = () => {
    if (groupSettings) {
      form.setFieldsValue(groupSettings)
    }
  }

  useEffect(getInitValues, [groupSettings])

  useEffect(() => {
    if (actualsSource === GroupSettingDataSource.subsidiaries && budgetSource === GroupSettingDataSource.group) {
      form.setFieldValue([ReportDataType.budget, 'source'], GroupSettingDataSource.subsidiaries)
    }
  }, [actualsSource, budgetSource])

  const budgetGroupDisabled = actualsSource === GroupSettingDataSource.subsidiaries

  return (
    <LoadingWrapper loading={isLoading}>
      <Form
        onFinish={handleSubmit}
        onValuesChange={(changed, allValues) => handleSubmit(allValues)}
        initialValues={groupSettings}
        form={form}
        style={{ maxWidth: 700 }}
        layout="vertical"
      >
        <Typography style={{ marginTop: 24 }}>
          <Form.Item label={t('subsidiaries:groupActuals')} name={['actuals', 'source']} rules={[{ required: true }]}>
            <Radio.Group>
              <Space direction="vertical">
                <Radio value={GroupSettingDataSource.group}>{t('subsidiaries:integration')}</Radio>
                <Radio value={GroupSettingDataSource.subsidiaries}>{t('subsidiaries:addUp')}</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          <Form.Item label={t('subsidiaries:groupBudget')} name={['budget', 'source']} rules={[{ required: true }]}>
            <Radio.Group>
              <Space direction="vertical">
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <Radio value={GroupSettingDataSource.group} disabled={budgetGroupDisabled}>
                    {t('subsidiaries:enter')}
                  </Radio>
                  {budgetGroupDisabled && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: 0,
                        right: 0,
                        height: 1,
                        backgroundColor: '#bfbfbf',
                        pointerEvents: 'none'
                      }}
                    />
                  )}
                </div>
                <Radio value={GroupSettingDataSource.subsidiaries}>{t('subsidiaries:addUp')}</Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          {budgetSource === GroupSettingDataSource.subsidiaries ? (
            <Alert
              style={{ marginBottom: 16 }}
              message={t('global:alertNoteTitle')}
              description={
                <div>
                  <p>{t('subsidiaries:budgetingInfoText1')}</p>
                  <div style={{ margin: '8px 0' }}>
                    <p style={{ margin: '2px 0' }}>{t('subsidiaries:budgetingInfoText2')}</p>
                    <p style={{ margin: '2px 0' }}>{t('subsidiaries:budgetingInfoText3')}</p>
                    <p style={{ margin: '2px 0' }}>{t('subsidiaries:budgetingInfoText4')}</p>
                  </div>
                  <p>{t('subsidiaries:budgetingInfoText5')}</p>
                </div>
              }
              type="info"
            />
          ) : null}
          {accountsRequired
            ? Object.entries(groupSettingsConfig).map(([key, value]) => (
                <Form.Item label={t(`subsidiaries:${key}`)} name={key}>
                  <AccountSelect allowClear balanceSheetRowIds={value} />
                </Form.Item>
              ))
            : null}
          <Typography.Title level={5}>{t('subsidiaries:groupBalanceSheet')}</Typography.Title>
          <Typography.Paragraph>{t('subsidiaries:balanceSheetIncomestatement')}</Typography.Paragraph>
        </Typography>
      </Form>
    </LoadingWrapper>
  )
}

export default Figures
